import React from 'react';
import { Button, Card, Row, Col, Typography, Space, Avatar } from 'antd';
import {
  HeartOutlined,
  SafetyOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ParticleBackground';

const { Title, Paragraph } = Typography;

const About = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const getThemeStyles = () => {
    if (theme === 'dark') {
      return {
        titleColor: '#ffffff',
        textColor: '#cccccc',
        subtitleColor: '#a0a0a0',
        cardBg: 'rgba(20, 20, 20, 0.8)',
        cardBorder: 'rgba(255, 255, 255, 0.2)',
        heroBg: 'linear-gradient(135deg, rgba(0, 0, 0, 0.9) 0%, rgba(40, 40, 40, 0.9) 100%)',
        sectionBg: 'rgba(0, 0, 0, 0.5)',
        accentColor: '#ffffff'
      };
    } else {
      return {
        titleColor: '#000000',
        textColor: '#333333',
        subtitleColor: '#666666',
        cardBg: 'rgba(255, 255, 255, 0.9)',
        cardBorder: 'rgba(0, 0, 0, 0.1)',
        heroBg: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.9) 100%)',
        sectionBg: 'rgba(248, 250, 252, 0.5)',
        accentColor: '#000000'
      };
    }
  };

  const themeStyles = getThemeStyles();

  const values = [
    {
      icon: <HeartOutlined style={{ fontSize: '32px', color: themeStyles.accentColor }} />,
      title: 'Holistic Healing',
      description: 'We address your complete well-being—physical, mental, emotional, and spiritual—not just symptoms.'
    },
    {
      icon: <SafetyOutlined style={{ fontSize: '32px', color: themeStyles.accentColor }} />,
      title: 'Nature\'s Wisdom',
      description: 'Aligning with the five elements (Earth, Water, Fire, Air, and Ether) to restore your natural balance.'
    },
    {
      icon: <TeamOutlined style={{ fontSize: '32px', color: themeStyles.accentColor }} />,
      title: 'Evidence-Based Practice',
      description: 'Combining traditional naturopathy and yoga with modern scientific understanding for sustainable results.'
    },
    {
      icon: <TrophyOutlined style={{ fontSize: '32px', color: themeStyles.accentColor }} />,
      title: 'Self-Healing Power',
      description: 'We believe the cure is within you. Your body has the blueprint for perfect health—we help you access it.'
    }
  ];

  const teamMembers = [
    {
      name: 'Dr. Abhinaya G',
      role: 'Founder & Proprietor',
      qualifications: 'BNYS, Fellowship in Acupuncture, ACOBG, CCIM',
      specialization: "Women's health, Pre-Postnatal Yoga and Garbhasanskar",
      image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?q=80&w=300&auto=format&fit=crop&ixlib=rb-4.0.3'
    }
  ];

  const stats = [
    { number: '500+', label: 'Lives Transformed' },
    { number: '15+', label: 'Years of Experience' },
    { number: '98%', label: 'Client Satisfaction' },
    { number: '24/7', label: 'Support Available' }
  ];

  const achievements = [
    'Certified Naturopathy Practitioners',
    'Personalized Treatment Plans',
    'Evidence-Based Natural Approaches',
    'Women\'s Health Specialists',
    'Pre & Postnatal Yoga Programs',
    'Holistic Lifestyle Guidance'
  ];

  return (
    <div style={{ minHeight: '100vh', background: themeStyles.heroBg }}>
      {/* Compact Hero Section */}
      <div style={{
        padding: '60px 24px 40px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated gradient background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: theme === 'dark'
            ? 'radial-gradient(circle at 50% 50%, rgba(102, 126, 234, 0.1) 0%, transparent 50%)'
            : 'radial-gradient(circle at 50% 50%, rgba(102, 126, 234, 0.05) 0%, transparent 50%)',
          animation: 'pulse 8s ease-in-out infinite',
          pointerEvents: 'none'
        }} />

        <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Sanskrit Verse - Compact */}
          <div style={{
            marginBottom: '25px',
            padding: '20px 25px',
            borderRadius: '16px',
            background: theme === 'dark'
              ? 'rgba(30, 41, 59, 0.6)'
              : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${theme === 'dark' ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.2)'}`,
            boxShadow: theme === 'dark'
              ? '0 0 30px rgba(102, 126, 234, 0.15), 0 10px 40px rgba(0, 0, 0, 0.2)'
              : '0 0 30px rgba(102, 126, 234, 0.08), 0 10px 40px rgba(0, 0, 0, 0.08)',
            animation: 'glow 4s ease-in-out infinite'
          }}>
            <Paragraph style={{
              fontSize: 'clamp(16px, 2.5vw, 20px)',
              fontStyle: 'italic',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              backgroundSize: '200% auto',
              animation: 'shimmer 3s linear infinite',
              margin: 0,
              lineHeight: '1.6',
              fontWeight: '600',
              fontFamily: 'Georgia, serif'
            }}>
              सत्त्वमात्मा शरीरं च त्रयमेतत् त्रिदण्डवत्। लोकस्तिष्ठति संयोगात्तत्र सर्वं प्रतिष्ठितम्॥
            </Paragraph>
          </div>

          {/* Title - Compact */}
          <Title level={1} style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '800',
            marginBottom: '20px',
            lineHeight: '1.1',
            letterSpacing: '-0.02em'
          }}>
            <span style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              display: 'inline-block'
            }}>About</span>{' '}
            <span style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              display: 'inline-block'
            }}>Ekah</span>
          </Title>

          {/* Belief Statement - Compact */}
          <div style={{
            marginBottom: '25px',
            padding: '20px 28px',
            borderRadius: '12px',
            background: theme === 'dark'
              ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15))'
              : 'linear-gradient(135deg, rgba(102, 126, 234, 0.08), rgba(118, 75, 162, 0.08))',
            border: `2px solid ${theme === 'dark' ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.2)'}`,
            boxShadow: theme === 'dark'
              ? '0 8px 30px rgba(102, 126, 234, 0.15)'
              : '0 8px 30px rgba(102, 126, 234, 0.08)'
          }}>
            <Paragraph style={{
              fontSize: 'clamp(15px, 2.5vw, 18px)',
              color: themeStyles.titleColor,
              margin: '0 0 8px 0',
              lineHeight: '1.5',
              fontWeight: '600'
            }}>
              We are <span style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Ekah (One)</span>, united by one powerful belief:
            </Paragraph>
            <Paragraph style={{
              fontSize: 'clamp(18px, 3vw, 24px)',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: 0,
              lineHeight: '1.3',
              fontWeight: '800',
              letterSpacing: '-0.01em'
            }}>
              Body heals itself
            </Paragraph>
          </div>

          {/* Description - Compact */}
          <Paragraph style={{
            fontSize: 'clamp(14px, 2vw, 16px)',
            color: themeStyles.textColor,
            maxWidth: '750px',
            margin: '0 auto 15px',
            lineHeight: '1.6'
          }}>
            Dedicated naturopaths supporting evidence-based, sustainable living. We empower you to live in harmony with the five elements and restore your natural balance through holistic health practices.
          </Paragraph>

          <Button
            type="primary"
            size="large"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '10px',
              padding: '12px 32px',
              height: 'auto',
              fontSize: '16px',
              fontWeight: '700',
              boxShadow: '0 6px 20px rgba(102, 126, 234, 0.25)',
              transition: 'all 0.3s ease'
            }}
            onClick={() => navigate('/booking')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1.03)';
              e.currentTarget.style.boxShadow = '0 10px 28px rgba(102, 126, 234, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.25)';
            }}
          >
            Start Your Journey
          </Button>
        </div>

        {/* Keyframes */}
        <style>{`
          @keyframes shimmer {
            0% { background-position: 0% center; }
            100% { background-position: 200% center; }
          }
          @keyframes glow {
            0%, 100% { 
              box-shadow: ${theme === 'dark'
            ? '0 0 30px rgba(102, 126, 234, 0.15), 0 10px 40px rgba(0, 0, 0, 0.2)'
            : '0 0 30px rgba(102, 126, 234, 0.08), 0 10px 40px rgba(0, 0, 0, 0.08)'};
            }
            50% { 
              box-shadow: ${theme === 'dark'
            ? '0 0 50px rgba(102, 126, 234, 0.3), 0 10px 40px rgba(0, 0, 0, 0.2)'
            : '0 0 50px rgba(102, 126, 234, 0.15), 0 10px 40px rgba(0, 0, 0, 0.08)'};
            }
          }
          @keyframes pulse {
            0%, 100% { opacity: 0.5; transform: scale(1); }
            50% { opacity: 0.8; transform: scale(1.1); }
          }
        `}</style>
      </div>

      {/* Our Story Section - Original Layout */}
      <div style={{ padding: '80px 24px', maxWidth: '1400px', margin: '0 auto', position: 'relative' }}>
        {/* Animated Background Vectors */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 0
        }}>
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                width: i % 3 === 0 ? '50px' : '40px',
                height: i % 3 === 0 ? '50px' : '40px',
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                opacity: theme === 'dark' ? (i % 2 === 0 ? 0.12 : 0.08) : (i % 2 === 0 ? 0.08 : 0.05),
                animation: `float ${12 + Math.random() * 15}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 5}s`,
                transform: `rotate(${Math.random() * 360}deg)`
              }}
            >
              <svg viewBox="0 0 24 24" fill={theme === 'dark' ? (i % 3 === 0 ? '#10b981' : '#667eea') : (i % 3 === 0 ? '#059669' : '#667eea')}>
                {i % 6 === 0 ? (
                  <path d="M12,2C13.1,2 14,2.9 14,4C14,5.1 13.1,6 12,6C10.9,6 10,5.1 10,4C10,2.9 10.9,2 12,2M15.9,8.1C15.5,7.7 14.8,7.7 14.4,8.1L12,10.5L9.6,8.1C9.2,7.7 8.5,7.7 8.1,8.1C7.7,8.5 7.7,9.2 8.1,9.6L11.3,12.8V22H12.8V12.8L16,9.6C16.4,9.2 16.4,8.5 15.9,8.1Z" />
                ) : i % 6 === 1 ? (
                  <path d="M12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22M12,4A8,8 0 0,0 4,12C4,13.85 4.63,15.55 5.68,16.91L8.05,13.58C7.42,12.92 7,12 7,11A5,5 0 0,1 12,6A5,5 0 0,1 17,11C17,12 16.58,12.92 15.95,13.58L18.32,16.91C19.37,15.55 20,13.85 20,12A8,8 0 0,0 12,4Z" />
                ) : i % 6 === 2 ? (
                  <path d="M12,21.35L10.55,20.03C5.4,15.36 2,12.27 2,8.5C2,5.41 4.42,3 7.5,3C9.24,3 10.91,3.81 12,5.08C13.09,3.81 14.76,3 16.5,3C19.58,3 22,5.41 22,8.5C22,12.27 18.6,15.36 13.45,20.03L12,21.35Z" />
                ) : i % 6 === 3 ? (
                  <path d="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z" />
                ) : i % 6 === 4 ? (
                  <path d="M12,18.5C10.67,18.5 9.67,17.5 9.67,16.17C9.67,15.67 9.83,15.17 10.17,14.83L12,13L13.83,14.83C14.17,15.17 14.33,15.67 14.33,16.17C14.33,17.5 13.33,18.5 12,18.5M17,13C17,14.5 16.2,15.72 15,16.5V20H13V17.5C12.67,17.5 12.33,17.5 12,17.5C11.67,17.5 11.33,17.5 11,17.5V20H9V16.5C7.8,15.72 7,14.5 7,13A5,5 0 0,1 12,8A5,5 0 0,1 17,13M12,10A3,3 0 0,0 9,13C9,13.68 9.25,14.29 9.67,14.75L12,12.42L14.33,14.75C14.75,14.29 15,13.68 15,13A3,3 0 0,0 12,10Z" />
                ) : (
                  <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M11,17V16H13V17H11M11,7V15H13V7H11Z" />
                )}
              </svg>
            </div>
          ))}
        </div>

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Section Title */}
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <Title level={2} style={{
              color: themeStyles.titleColor,
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              marginBottom: '16px'
            }}>
              Our Story: The Heartbeat of Ekahlife
            </Title>
            <Paragraph style={{
              color: themeStyles.subtitleColor,
              fontSize: '18px',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              A journey from struggle to healing, from one to many
            </Paragraph>
          </div>

          {/* First Row: Image Left, Text Right */}
          <Row gutter={[48, 48]} align="middle" style={{ marginBottom: '60px' }}>
            <Col xs={24} lg={10}>
              <div style={{
                background: `url('https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=800&auto=format&fit=crop') center/cover`,
                height: '450px',
                borderRadius: '24px',
                position: 'relative',
                boxShadow: theme === 'dark'
                  ? '0 20px 60px rgba(0, 0, 0, 0.4)'
                  : '0 20px 60px rgba(0, 0, 0, 0.15)',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.25), rgba(118, 75, 162, 0.25))',
                  borderRadius: '24px'
                }} />
              </div>
            </Col>
            <Col xs={24} lg={14}>
              <div style={{
                background: theme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.9)',
                borderRadius: '24px',
                padding: '40px',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${themeStyles.cardBorder}`,
                boxShadow: theme === 'dark'
                  ? '0 20px 60px rgba(0, 0, 0, 0.3)'
                  : '0 20px 60px rgba(0, 0, 0, 0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Title level={3} style={{
                  color: themeStyles.titleColor,
                  marginBottom: '20px',
                  fontSize: '24px'
                }}>
                  I Am One
                </Title>
                <Paragraph style={{
                  color: themeStyles.textColor,
                  fontSize: '17px',
                  lineHeight: '1.8',
                  marginBottom: '20px'
                }}>
                  The story of Ekahlife is born from a quiet, internal realization: <em>I am one.</em>
                </Paragraph>
                <Paragraph style={{
                  color: themeStyles.textColor,
                  fontSize: '17px',
                  lineHeight: '1.8',
                  marginBottom: 0
                }}>
                  For years, I experienced the exhausting journey of a personal health struggle. Like many, I faced hormonal imbalance and PCOS, and I discovered that no single pill could mend a disconnect that ran deeper than the physical body. It felt like a classic case of Adhija Vyadhi—a disease that arose because the essential connection between my body and my mind had been broken.
                </Paragraph>
              </div>
            </Col>
          </Row>

          {/* Second Row: Text Left, Image Right */}
          <Row gutter={[48, 48]} align="middle" style={{ marginBottom: '60px' }}>
            <Col xs={24} lg={14}>
              <div style={{
                background: theme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.9)',
                borderRadius: '24px',
                padding: '40px',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${themeStyles.cardBorder}`,
                boxShadow: theme === 'dark'
                  ? '0 20px 60px rgba(0, 0, 0, 0.3)'
                  : '0 20px 60px rgba(0, 0, 0, 0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Title level={3} style={{
                  color: themeStyles.titleColor,
                  marginBottom: '20px',
                  fontSize: '24px'
                }}>
                  The Cure Within
                </Title>
                <Paragraph style={{
                  color: themeStyles.textColor,
                  fontSize: '17px',
                  lineHeight: '1.8',
                  marginBottom: '20px'
                }}>
                  My true turning point wasn't found in a prescription, but in a profound shift of perspective. When I joined as a Naturopathy student, I began to truly live by what I was learning: aligning my diet, my actions, and my entire lifestyle with nature's wisdom.
                </Paragraph>
                <Paragraph style={{
                  color: themeStyles.textColor,
                  fontSize: '17px',
                  lineHeight: '1.8',
                  marginBottom: 0
                }}>
                  The result was my personal health revolution—my health was completely and naturally reversed.
                </Paragraph>
              </div>
            </Col>
            <Col xs={24} lg={10}>
              <div style={{
                background: `url('https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=800&auto=format&fit=crop') center/cover`,
                height: '450px',
                borderRadius: '24px',
                position: 'relative',
                boxShadow: theme === 'dark'
                  ? '0 20px 60px rgba(0, 0, 0, 0.4)'
                  : '0 20px 60px rgba(0, 0, 0, 0.15)',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.25), rgba(5, 150, 105, 0.25))',
                  borderRadius: '24px'
                }} />
              </div>
            </Col>
          </Row>

          {/* Highlighted Quote Section */}
          <div style={{
            background: theme === 'dark'
              ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2))'
              : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))',
            borderRadius: '24px',
            padding: '50px 40px',
            textAlign: 'center',
            marginBottom: '60px',
            border: `2px solid ${themeStyles.accentColor}40`,
            boxShadow: theme === 'dark'
              ? '0 20px 60px rgba(102, 126, 234, 0.2)'
              : '0 20px 60px rgba(102, 126, 234, 0.15)'
          }}>
            <Title level={3} style={{
              color: themeStyles.titleColor,
              fontSize: 'clamp(1.5rem, 4vw, 2rem)',
              fontStyle: 'italic',
              fontWeight: '600',
              margin: 0,
              lineHeight: '1.4'
            }}>
              "This powerful journey taught me the ultimate truth: the cure is in you; it will come from within."
            </Title>
          </div>

          {/* Third Row: Image Left, Text Right */}
          <Row gutter={[48, 48]} align="middle">
            <Col xs={24} lg={10}>
              <div style={{
                background: `url('https://images.unsplash.com/photo-1545389336-cf090694435e?q=80&w=800&auto=format&fit=crop') center/cover`,
                height: '450px',
                borderRadius: '24px',
                position: 'relative',
                boxShadow: theme === 'dark'
                  ? '0 20px 60px rgba(0, 0, 0, 0.4)'
                  : '0 20px 60px rgba(0, 0, 0, 0.15)',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.3), rgba(118, 75, 162, 0.3))',
                  borderRadius: '24px'
                }} />
              </div>
            </Col>
            <Col xs={24} lg={14}>
              <div style={{
                background: theme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.9)',
                borderRadius: '24px',
                padding: '40px',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${themeStyles.cardBorder}`,
                boxShadow: theme === 'dark'
                  ? '0 20px 60px rgba(0, 0, 0, 0.3)'
                  : '0 20px 60px rgba(0, 0, 0, 0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Title level={3} style={{
                  color: themeStyles.titleColor,
                  marginBottom: '20px',
                  fontSize: '24px'
                }}>
                  Ekah - We Are One
                </Title>
                <Paragraph style={{
                  color: themeStyles.textColor,
                  fontSize: '17px',
                  lineHeight: '1.8',
                  marginBottom: '20px'
                }}>
                  At Ekah, which means "One"—one life, one health—we don't just treat symptoms. We understand that your body is already encoded with the blueprint for perfect health. We simply help you access it.
                </Paragraph>
                <Paragraph style={{
                  color: themeStyles.textColor,
                  fontSize: '17px',
                  lineHeight: '1.8',
                  marginBottom: 0
                }}>
                  We act as your guides, helping you reconnect your body and mind through a blend of physical, mental, emotional, and spiritual practices. We work with the magic of the five elements to restore your natural balance, ensuring that your path to wellness is holistic, authentic, and truly your own.
                </Paragraph>
              </div>
            </Col>
          </Row>
        </div>

        {/* Float animation */}
        <style>{`
          @keyframes float {
            0%, 100% {
              transform: translateY(0px) translateX(0px) rotate(0deg);
            }
            25% {
              transform: translateY(-20px) translateX(10px) rotate(90deg);
            }
            50% {
              transform: translateY(-10px) translateX(-10px) rotate(180deg);
            }
            75% {
              transform: translateY(-25px) translateX(5px) rotate(270deg);
            }
          }
        `}</style>
      </div>

      {/* Values Section - Unified Background */}
      <div style={{ padding: '50px 24px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <Title level={2} style={{ color: themeStyles.titleColor, marginBottom: '16px' }}>
            Our Core Principles
          </Title>
          <Paragraph style={{
            color: themeStyles.subtitleColor,
            fontSize: '18px',
            marginBottom: '60px',
            maxWidth: '600px',
            margin: '0 auto 60px'
          }}>
            The foundations of our naturopathy and yoga practice
          </Paragraph>

          <Row gutter={[32, 32]}>
            {values.map((value, index) => (
              <Col xs={24} sm={12} lg={6} key={index}>
                <Card
                  style={{
                    background: themeStyles.cardBg,
                    border: `1px solid ${themeStyles.cardBorder}`,
                    borderRadius: '16px',
                    textAlign: 'center',
                    height: '100%',
                    backdropFilter: 'blur(8px)'
                  }}
                  bodyStyle={{ padding: '32px 24px' }}
                  hoverable
                >
                  <div style={{ marginBottom: '20px' }}>{value.icon}</div>
                  <Title level={4} style={{ color: themeStyles.titleColor, marginBottom: '16px' }}>
                    {value.title}
                  </Title>
                  <Paragraph style={{ color: themeStyles.textColor, margin: 0 }}>
                    {value.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* Team Section - Unified Background */}
      <div style={{ padding: '50px 24px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <Title level={2} style={{ color: themeStyles.titleColor, marginBottom: '12px', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)' }}>
            Meet Our Founder
          </Title>
          <Paragraph style={{
            color: themeStyles.subtitleColor,
            fontSize: '16px',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Dedicated to guiding you on your journey to holistic wellness
          </Paragraph>
        </div>

        <Row gutter={[32, 32]} justify="center">
          {teamMembers.map((member, index) => (
            <Col xs={24} sm={20} md={12} lg={10} key={index}>
              <Card
                style={{
                  background: theme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.9)',
                  border: `1px solid ${themeStyles.cardBorder}`,
                  borderRadius: '16px',
                  textAlign: 'center',
                  backdropFilter: 'blur(10px)',
                  boxShadow: theme === 'dark'
                    ? '0 10px 40px rgba(0, 0, 0, 0.2)'
                    : '0 10px 40px rgba(0, 0, 0, 0.08)'
                }}
                bodyStyle={{ padding: '32px 28px' }}
                hoverable
              >
                <Avatar
                  size={120}
                  src={member.image}
                  style={{ marginBottom: '24px' }}
                />
                <Title level={4} style={{ color: themeStyles.titleColor, marginBottom: '8px', fontSize: '20px' }}>
                  {member.name}
                </Title>
                <Paragraph style={{
                  color: themeStyles.accentColor,
                  fontWeight: '700',
                  marginBottom: '12px',
                  fontSize: '16px'
                }}>
                  {member.role}
                </Paragraph>
                {member.qualifications && (
                  <Paragraph style={{ color: themeStyles.textColor, marginBottom: '12px', fontSize: '14px' }}>
                    {member.qualifications}
                  </Paragraph>
                )}
                <Paragraph style={{ color: themeStyles.textColor, marginBottom: 0, fontSize: '15px', lineHeight: '1.6' }}>
                  <strong>Specialized in:</strong> {member.specialization}
                </Paragraph>
              </Card>
            </Col>
          ))}
        </Row>
      </div>


      {/* Achievements Section - Unified Background */}
      <div style={{ padding: '50px 24px', maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <Title level={2} style={{ color: themeStyles.titleColor, marginBottom: '12px', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)' }}>
            Why Choose Ekah?
          </Title>
          <Paragraph style={{
            color: themeStyles.subtitleColor,
            fontSize: '16px'
          }}>
            Our commitment to your holistic wellness journey
          </Paragraph>
        </div>

        <Row gutter={[20, 20]}>
          {achievements.map((achievement, index) => (
            <Col xs={24} sm={12} key={index}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                padding: '18px',
                background: theme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.9)',
                borderRadius: '12px',
                border: `1px solid ${themeStyles.cardBorder}`,
                backdropFilter: 'blur(10px)',
                boxShadow: theme === 'dark'
                  ? '0 8px 30px rgba(0, 0, 0, 0.15)'
                  : '0 8px 30px rgba(0, 0, 0, 0.06)'
              }}>
                <CheckCircleOutlined
                  style={{
                    fontSize: '22px',
                    color: '#10b981',
                    flexShrink: 0
                  }}
                />
                <Paragraph style={{
                  color: themeStyles.textColor,
                  margin: 0,
                  fontSize: '15px',
                  lineHeight: '1.5'
                }}>
                  {achievement}
                </Paragraph>
              </div>
            </Col>
          ))}
        </Row>
      </div>


    </div>
  );
};

export default About;
